import { type Duration, formatDuration as durationFormat, intervalToDuration } from 'date-fns';

import { isNumber } from '#number/is-number';
import { padStart } from '#string/pad-start';

import type { FormatDurationOptions } from './types/format-duration.options';

const DEFAULT_DURATION = {
  days: 0,
  hours: 0,
  minutes: 0,
  months: 0,
  seconds: 0,
  years: 0,
} satisfies Duration;

/**
 * Formats a duration in milliseconds or a `Duration` object into a string.
 *
 * Two output styles are available via the `format` option:
 * - `'clock'` (default) — zero-padded numeric segments. All superior time units overflow into
 *   the leading segment: hours when `showHours` is `true` (default), minutes when `false`.
 *   Years and months use approximate lengths (365 days/year, 30 days/month).
 * - `'human'` — natural-language description, e.g. `"1 day 1 hour 23 minutes"`.
 *   Zero-value components are omitted; a zero duration returns `"0 seconds"`.
 *
 * @param milliseconds - The duration to format, either as a millisecond count or a `date-fns` `Duration` object.
 * @param options - Formatting options. See {@link FormatDurationOptions}.
 * @returns The formatted duration string.
 *
 * @example
 * ```ts
 * formatDuration(3661000)                              // "01:01:01"
 * formatDuration(90000000)                             // "25:00:00" (25 h overflows into hours)
 * formatDuration(3661000,   { showHours: false })      // "61:01"
 * formatDuration(90000000,  { showHours: false })      // "1500:00" (25 h overflows into minutes)
 * formatDuration(3661000,   { format: 'human' })       // "1 hour 1 minute 1 second"
 * formatDuration(90000000,  { format: 'human' })       // "1 day 1 hour"
 * formatDuration(0,         { format: 'human' })       // "0 seconds"
 * ```
 */
export const formatDuration = (milliseconds: Duration | number, options: FormatDurationOptions = {}) => {
  const { format = 'clock', showHours = true } = options;
  const duration = isNumber(milliseconds) ? intervalToDuration({ end: milliseconds, start: 0 }) : milliseconds;
  const normalized = { ...DEFAULT_DURATION, ...duration };

  if (format === 'human') {
    const result = durationFormat(normalized, {
      delimiter: ' ',
      format: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
      zero: false,
    });

    return result.length > 0 ? result : '0 seconds';
  }

  // Years and months use approximate lengths (365 days/year, 30 days/month).
  const totalDays = normalized.years * 365 + normalized.months * 30 + normalized.days;

  if (showHours) {
    const totalHours = totalDays * 24 + normalized.hours;
    const hours = padStart(String(totalHours), 2, '0');
    const minutes = padStart(String(normalized.minutes), 2, '0');
    const seconds = padStart(String(normalized.seconds), 2, '0');

    return `${hours}:${minutes}:${seconds}`;
  }

  const totalMinutes = totalDays * 24 * 60 + normalized.hours * 60 + normalized.minutes;
  const minutes = padStart(String(totalMinutes), 2, '0');
  const seconds = padStart(String(normalized.seconds), 2, '0');

  return `${minutes}:${seconds}`;
};
